(() => {
  if (customElements.get('saadaa-offer-rotation')) return;

  class SaadaaOfferRotation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.index = 0;
      this.hovered = false;
      this.focused = false;
      this.inView = false;
      this.copyTimers = new Set();
      this.slides = [...this.querySelector('.sr__slides').children].filter(el => el.hasAttribute('data-offer-slide'));
      const options = { signal: this.controller.signal };
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse' && this.classList.contains('sr--product')) { this.hovered = true; this.schedule(); }
      }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, options);
      this.addEventListener('focusin', () => { this.focused = true; this.schedule(); }, options);
      this.addEventListener('focusout', () => {
        queueMicrotask(() => {
          this.focused = this.contains(document.activeElement);
          this.schedule();
        });
      }, options);
      document.addEventListener('visibilitychange', () => { this.refresh(); }, options);
      if (this.dataset.productId) {
        document.addEventListener('variant:change', event => {
          // Each quick-buy/product instance only follows its own product and form.
          const source = event.target.closest?.('.product, .quick-buy-modal, quick-buy-modal');
          const own = this.closest('.product, .quick-buy-modal, quick-buy-modal');
          if (source && own && source !== own) return;
          if (String(event.detail?.product?.id) !== this.dataset.productId) return;
          const price = event.detail?.variant?.price;
          if (!Number.isFinite(price)) return;
          this.dataset.price = String(price);
          const net = this.querySelector('[data-net-price]');
          if (net) net.textContent = '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Math.max(0, price - 11100) / 100);
          const get2Net = this.querySelector('[data-get2-net-price]');
          if (get2Net) get2Net.textContent = '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Math.max(0, price - 10000) / 100);
          this.refresh();
        }, options);
      }
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries.some(entry => entry.isIntersecting);
        this.refresh();
      });
      this.observer.observe(this);
      this.setAttribute('data-ready', '');
      this.refresh();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.expiryTimer);
      this.copyTimers?.forEach(timer => clearTimeout(timer));
    }

    refresh() {
      const now = Date.now();
      clearTimeout(this.expiryTimer);
      let nextExpiry = Infinity;
      const current = this.available?.[this.index];
      this.available = this.slides.filter(slide => {
        const expires = slide.dataset.expires ? Date.parse(slide.dataset.expires) : Infinity;
        const price = Number(this.dataset.price);
        const tooCheap = Boolean(this.dataset.productId) && price <= 20000;
        const unavailable = expires <= now || tooCheap;
        slide.toggleAttribute('data-unavailable', unavailable);
        if (!unavailable && Number.isFinite(expires)) nextExpiry = Math.min(nextExpiry, expires);
        return !unavailable;
      });
      this.hidden = this.available.length === 0;
      const preservedIndex = this.available.indexOf(current);
      this.show(preservedIndex < 0 ? 0 : preservedIndex);
      if (Number.isFinite(nextExpiry)) {
        this.expiryTimer = setTimeout(() => this.refresh(), Math.min(nextExpiry - now + 20, 2147483647));
      }
      this.schedule();
    }

    show(index) {
      this.index = index;
      const active = this.available[index];
      this.slides.forEach(slide => {
        const inactive = slide !== active;
        slide.setAttribute('aria-hidden', String(inactive));
        slide.inert = inactive;
      });

    }

    schedule() {
      clearTimeout(this.timer);
      const state = !this.isConnected ? 'detached' : this.available?.length < 2 ? 'single-slide' : document.hidden ? 'background' : !this.inView ? 'offscreen' : this.focused ? 'focused' : this.hovered ? 'hovered' : 'running';
      this.dataset.rotationState = state;
      if (state !== 'running') return;
      this.timer = setTimeout(() => {
        // Refresh eligibility at each tick, even after sleeping/background throttling.
        this.refresh();
        this.show((this.index + 1) % this.available.length);
        this.schedule();
      }, 3000);
    }

    onClick(event) {
      const button = event.target.closest('[data-copy-code]');
      if (button && this.contains(button)) this.copyCode(button);
    }

    async copyCode(button) {
      // Capture code now: a later slide must never change what this click copies.
      const code = button.dataset.copyCode;
      let success = false;
      if (navigator.clipboard?.writeText) {
        try { await navigator.clipboard.writeText(code); success = true; } catch (_) { /* Try legacy fallback. */ }
      }
      if (!success) {
        const input = document.createElement('textarea');
        input.value = code;
        input.setAttribute('readonly', '');
        input.style.cssText = 'position:fixed;left:-9999px;top:0';
        document.body.append(input);
        input.select();
        try { success = document.execCommand('copy'); } catch (_) { /* Report failure truthfully. */ }
        input.remove();
        button.focus({ preventScroll: true });
      }
      if (!this.isConnected) return;
      const label = button.querySelector('[data-copy-label]');
      label.textContent = success ? button.dataset.copied : button.dataset.failed;
      button.parentElement.querySelector('[data-copy-status]').textContent = success ? `${code}: ${button.dataset.copied}` : `${button.dataset.failed}: ${code}`;
      const timer = setTimeout(() => {
        label.textContent = button.dataset.label;
        this.copyTimers.delete(timer);
      }, 1800);
      this.copyTimers.add(timer);
    }
  }

  customElements.define('saadaa-offer-rotation', SaadaaOfferRotation);

  // Static header coupon shares the proven copy/fallback/status behavior with product cards.
  class SaadaaHeaderOffer extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.copyTimers = new Set();
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-copy-code]');
        if (button && this.contains(button)) SaadaaOfferRotation.prototype.copyCode.call(this, button);
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.copyTimers?.forEach(timer => clearTimeout(timer));
    }
  }
  if (!customElements.get('saadaa-header-offer')) customElements.define('saadaa-header-offer', SaadaaHeaderOffer);


  // 2026-10-17: Keep original money-back strips static; product offer rotation remains enabled.

})();
